import { CombinedState, createSelector } from '@reduxjs/toolkit'
import { useFlag, useLDClient } from '@afterpaytouch/feature'
import { useEffect, useRef, useState } from 'react'
import { Cohort } from '@afterpaytouch/portal-api'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { PaymentType } from '@afterpay/types'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import {
  CheckoutAppSelector,
  CheckoutAppState,
  CheckoutAppStore,
  ExperimentsState,
  triggerExperiment,
  useActiveCashAppPay,
  useAppDispatch,
  useAppSelector,
  useCashLoginType,
  useCohort,
  useConsumerFeatureFlagReady,
  useConsumerLendingEligible,
  useConsumerSelector,
  useIsEmptyCheckoutToken,
  useIsOneOfferPageQualified,
  useIsRecognizedViaPersistentLogin,
  useIsTrustedCheckout,
  usePreferredCard,
  useShouldSetApplePayAsPurchaseMethod,
  useShouldUseBankAccount,
} from '../'
import { Experiment, Experiments, ExperimentValue, Features, getExperiment, KnownExperiment } from '../../model'
import { useStore } from 'react-redux'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { consumeInteractiveLoginSignal, peekInteractiveLoginSignal } from '../../flow/remember-me/loginSignal'

export const experimentsSelector: CheckoutAppSelector<ExperimentsState> = (state) => state.experiments

export const activeExperiments: CheckoutAppSelector<string> = createSelector(experimentsSelector, ({ activeExperiments }) => {
  return Object.entries(activeExperiments)
    .map(([experiment, value]) => `${experiment}${value}`)
    .join(',')
})

const activeExperimentsSelector = (state: CombinedState<CheckoutAppState>): Partial<Record<KnownExperiment, ExperimentValue>> => {
  return state.experiments.activeExperiments
}

export const useExperimentsState = (): ExperimentsState => useAppSelector(experimentsSelector)

export const useActiveExperiments = (): string => useAppSelector(activeExperiments)

export const useTriggerExperimentEvent = (): ((params: Experiment, customEventProperties?: Record<string, unknown>) => void) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const store: CheckoutAppStore = useStore()
  const isEmptyCheckoutToken: boolean = useIsEmptyCheckoutToken()
  return ({ name, value, control }: Experiment, customEventProperties?: Record<string, unknown>): void => {
    const activeExperiments = activeExperimentsSelector(store.getState())
    const analyticsFired = name in activeExperiments
    if (!analyticsFired && !isEmptyCheckoutToken) {
      dispatch(triggerExperiment([name, value]))
      logEvent('Triggered Experiment', {
        experimentName: Experiments[name],
        experimentValue: value,
        experimentControl: control,
        experimentFlag: `${name}${value}`,
        ...customEventProperties,
      })
    }
  }
}

export const useExperimentTrigger = (experiment: Experiment): ((customEventProperties?: Record<string, unknown>) => void) => {
  const [fire, setFired] = useState(false)
  const [customEventProperties, setCustomEventProperties] = useState({})
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (experiment.active && !experiment.loading && fire) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }, [experiment.name, experiment.active, experiment.value, fire, triggerExperimentEvent, experiment, customEventProperties])

  return (customEventProperties) => {
    setFired(true)
    if (customEventProperties !== undefined) {
      setCustomEventProperties(customEventProperties)
    }
  }
}

export const useDelayedExperimentTrigger = (): ((updatedExperiment: Experiment) => void) => {
  const [{ isFired, givenExperiment }, setFiredExperiment] = useState<{ isFired: boolean; givenExperiment: Experiment | undefined }>({
    isFired: false,
    givenExperiment: undefined,
  })
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (givenExperiment === undefined) {
      return
    }

    if (givenExperiment.active && !givenExperiment.loading && isFired) {
      triggerExperimentEvent(givenExperiment)
    }
  }, [givenExperiment, givenExperiment?.name, givenExperiment?.active, givenExperiment?.value, givenExperiment?.control, isFired, triggerExperimentEvent])

  return (experiment) =>
    setFiredExperiment({
      isFired: true,
      givenExperiment: experiment,
    })
}

export function useExperiment(name: KnownExperiment, fireEvent: boolean = true, customisedExperiment: Experiment | null = null): Experiment {
  const client = useLDClient()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const { activeExperiments } = useExperimentsState()
  let experiment = customisedExperiment
  if (experiment === null) {
    const ldValue = consumerFeatureFlagReady ? client?.variationDetail(Experiments[name], false) : undefined
    experiment = getExperiment(name, ldValue, activeExperiments[name])
  }

  const experimentTrigger = useExperimentTrigger(experiment)

  useEffect(() => {
    if (fireEvent && consumerFeatureFlagReady) {
      experimentTrigger()
    }
  }, [consumerFeatureFlagReady, experimentTrigger, fireEvent])

  return experiment
}

export const useTriggerRememberMeExperimentExposure = (): void => {
  const experiment = useExperiment('AutoRememberMe', false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const isEmptyCheckoutToken = useIsEmptyCheckoutToken()
  const { uuid, hasFeatureFlagsReady } = useConsumerSelector()
  const isRecognizedViaPersistentLogin = useIsRecognizedViaPersistentLogin()
  const isTrustedCheckout = useIsTrustedCheckout()
  const ldClient = useLDClient()

  useEffect(() => {
    if (!peekInteractiveLoginSignal('remember-me-exposure')) {
      return
    }
    if (isRecognizedViaPersistentLogin || isTrustedCheckout) {
      consumeInteractiveLoginSignal('remember-me-exposure')
      return
    }
    if (isUndefinedOrEmpty(uuid) || ldClient === undefined) {
      return
    }
    const context = ldClient.getContext?.() as { key?: string } | undefined
    if (context?.key !== uuid || hasFeatureFlagsReady !== true) {
      return
    }
    if (experiment.loading || !experiment.active || isEmptyCheckoutToken) {
      return
    }
    consumeInteractiveLoginSignal('remember-me-exposure')
    const { name, value, control } = experiment
    dispatch(triggerExperiment([name, value]))
    logEvent('Triggered Experiment', {
      experimentName: Experiments[name],
      experimentValue: value,
      experimentControl: control,
      experimentFlag: `${name}${value}`,
      exposureTrigger: 'login',
    })
  }, [uuid, hasFeatureFlagsReady, ldClient, isEmptyCheckoutToken, isRecognizedViaPersistentLogin, isTrustedCheckout, experiment, dispatch, logEvent])
}

export const useTriggerCashInlineLoginExperimentExposure = (): void => {
  const experiment = useExperiment('CashInlineLogin', false)
  const loginType = useCashLoginType()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const triggerExperimentEvent = useTriggerExperimentEvent()
  const hasFiredRef = useRef(false)

  useEffect(() => {
    if (hasFiredRef.current) {
      return
    }
    if (loginType !== 'inline' || !hasFeatureFlagsReady || experiment.loading || !experiment.active) {
      return
    }
    hasFiredRef.current = true
    triggerExperimentEvent(experiment, { exposureTrigger: 'cash-auth' })
  }, [loginType, hasFeatureFlagsReady, experiment, triggerExperimentEvent])
}

export const useTriggerOneOfferPageExperimentExposure = (): void => {
  const experiment = useExperiment('OneOfferPage', false)
  const isOneOfferPageQualified = useIsOneOfferPageQualified()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const triggerExperimentEvent = useTriggerExperimentEvent()
  const hasFiredRef = useRef(false)

  useEffect(() => {
    if (hasFiredRef.current) {
      return
    }
    if (!isOneOfferPageQualified || !hasFeatureFlagsReady || experiment.loading || !experiment.active) {
      return
    }
    hasFiredRef.current = true
    triggerExperimentEvent(experiment, { exposureTrigger: 'route-resolved' })
  }, [isOneOfferPageQualified, hasFeatureFlagsReady, experiment, triggerExperimentEvent])
}

export const useIsEligibleForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    addPaymentMethodFlowExperimentValue === 'A',
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    !isPaymentMethodEligible,
    hasNoUsablePaymentMethod,
  ]
  return eligibilityConditions.every((condition) => condition)
}

export const useIsControlForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value, active, loading } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  return active && !loading && value === 'C' && cohort !== Cohort.CashOnly && !isBronteDesignLanguage && !isPaymentMethodEligible && hasNoUsablePaymentMethod
}

const usePi4SwitcherNoPaymentMethodExperimentEligibility = (
  selectedPaymentType?: PaymentType
): { eligible: boolean; experimentValue: ExperimentValue; experimentActive: boolean; experimentLoading: boolean } => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue, active, loading } = experiment
  const isPi4SwitcherSummaryFirstEnabled = useFlag(Features.CHECKOUT_ENABLE_ADD_PAYMENT_METHOD_PM_PI4_SWITCHER_SUMMARY_FIRST, false)
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    isPi4SwitcherSummaryFirstEnabled,
    selectedPaymentType === PaymentType.PBI,
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    isConsumerLendingEligible,
    hasNoUsablePaymentMethod,
  ]

  return {
    eligible: eligibilityConditions.every((condition) => condition),
    experimentValue: addPaymentMethodFlowExperimentValue,
    experimentActive: active,
    experimentLoading: loading,
  }
}

export const useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment = (selectedPaymentType?: PaymentType): boolean => {
  const { eligible, experimentValue, experimentActive, experimentLoading } = usePi4SwitcherNoPaymentMethodExperimentEligibility(selectedPaymentType)

  return eligible && experimentActive && !experimentLoading && (experimentValue === 'A' || experimentValue === 'C')
}

export const useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary = (selectedPaymentType?: PaymentType): boolean => {
  const { eligible, experimentValue, experimentActive, experimentLoading } = usePi4SwitcherNoPaymentMethodExperimentEligibility(selectedPaymentType)

  return eligible && experimentActive && !experimentLoading && experimentValue === 'A'
}

export const useAddPaymentMethodFlowExperimentTrigger = (): ((customEventProperties?: Record<string, unknown>) => void) => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const triggerExperimentEvent = useTriggerExperimentEvent()
  return (customEventProperties) => {
    if (experiment.active && !experiment.loading) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }
}
